/**
 * Phase 4D — per-job AbortController registry + cancellation eligibility.
 * One controller per generation job id (supports x11/x111). Not a global singleton.
 */

export type GenerationAbortHandle = {
  controller: AbortController;
  signal: AbortSignal;
};

const controllersByJobId = new Map<string, AbortController>();
const cancelledJobIds = new Set<string>();

export function isAbortError(error: unknown): boolean {
  if (!error) return false;
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return error.name === 'AbortError';
  }
  const e = error as { name?: string; message?: string };
  return e.name === 'AbortError' || /aborted|AbortError/i.test(String(e.message || ''));
}

/** Create (or replace) an AbortController for a generation job. */
export function beginGenerationAbort(jobId: string): GenerationAbortHandle {
  const existing = controllersByJobId.get(jobId);
  if (existing) {
    try {
      existing.abort();
    } catch {
      /* ignore */
    }
  }
  cancelledJobIds.delete(jobId);
  const controller = new AbortController();
  controllersByJobId.set(jobId, controller);
  return { controller, signal: controller.signal };
}

export function getGenerationAbortSignal(jobId: string): AbortSignal | undefined {
  return controllersByJobId.get(jobId)?.signal;
}

/** Abort fetch for a job and mark it cancelled so late results are discarded. */
export function abortGenerationJob(jobId: string): void {
  cancelledJobIds.add(jobId);
  const controller = controllersByJobId.get(jobId);
  if (controller) {
    try {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    } catch {
      /* ignore */
    }
  }
}

export function markGenerationCancelled(jobId: string): void {
  cancelledJobIds.add(jobId);
}

export function markGenerationsCancelled(jobIds: string[]): void {
  for (const id of jobIds) cancelledJobIds.add(id);
}

export function isGenerationCancelled(jobId: string): boolean {
  return cancelledJobIds.has(jobId);
}

/** True if this job may still append artwork / attach preview. */
export function isGenerationEligible(jobId: string): boolean {
  return !cancelledJobIds.has(jobId);
}

/** Drop controller after job finishes (success, failure, or abort). Keeps cancelled mark. */
export function releaseGenerationAbort(jobId: string): void {
  controllersByJobId.delete(jobId);
}

/** Clear cancelled marks for jobs that are fully gone (optional housekeeping). */
export function clearGenerationCancelled(jobId: string): void {
  cancelledJobIds.delete(jobId);
}

export function getGenerationAbortDiagnostics(): {
  activeControllers: number;
  cancelledCount: number;
} {
  return {
    activeControllers: controllersByJobId.size,
    cancelledCount: cancelledJobIds.size,
  };
}

/** Test-only reset. */
export function __resetGenerationAbortRegistryForTests(): void {
  for (const c of controllersByJobId.values()) {
    try {
      if (!c.signal.aborted) c.abort();
    } catch {
      /* ignore */
    }
  }
  controllersByJobId.clear();
  cancelledJobIds.clear();
}
